import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  TenantUrlProvider,
  useLocation,
  useRawLocation,
  Link,
} from './routing';
import TenantGate from './routing/TenantGate';
import LegacyRedirect from './routing/LegacyRedirect';
import { trackPageview } from './utils/analytics';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import Navbar from './components/Navbar';
import ViewAsBanner from './components/ViewAsBanner';
import PageWatermark from './components/PageWatermark';
import { useToast, ToastContainer } from './components/Toast';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDocumentsPage from './pages/SupplierDocumentsPage';
import DocumentViewPage from './pages/DocumentViewPage';
import UploadDocumentPage from './pages/UploadDocumentPage';
import AnalysisPage from './pages/AnalysisPage';
import SupplierPurchaseReportPage from './pages/SupplierPurchaseReportPage';
import ItemClassificationReportPage from './pages/ItemClassificationReportPage';
import TopProductsReportPage from './pages/TopProductsReportPage';
import RevenueVsExpensesReportPage from './pages/RevenueVsExpensesReportPage';
import SupplierManagementPage from './pages/SupplierManagementPage';
import MergeSuppliersPage from './pages/MergeSuppliersPage';
import CreditsOpenPage from './pages/CreditsOpenPage';
import CreditsHistoryPage from './pages/CreditsHistoryPage';
import PaymentsPage from './pages/PaymentsPage';
import DocumentsPage from './pages/DocumentsPage';
import DraftsPage from './pages/DraftsPage';
import UnpricedItemsPage from './pages/UnpricedItemsPage';
import DishesPage from './pages/DishesPage';
import DishDetailPage from './pages/DishDetailPage';
import ItemClassificationPage from './pages/ItemClassificationPage';
import CategoriesManagementPage from './pages/CategoriesManagementPage';
import CommandCenterLayout from './pages/command-center/CommandCenterLayout';
import CommandCenterOverviewPage from './pages/command-center/OverviewPage';
import CommandCenterPeoplePage from './pages/command-center/PeoplePage';
import CommandCenterUserCardPage from './pages/command-center/UserCardPage';
import CommandCenterCustomersPage from './pages/command-center/CustomersPage';
import CommandCenterCustomerCardPage from './pages/command-center/CustomerCardPage';
import CommandCenterQueuesPage from './pages/command-center/QueuesPage';
import CommandCenterStatsPage from './pages/command-center/StatsPage';
import CommandCenterBusinessPage from './pages/command-center/BusinessPage';
import CommandCenterLineItemsPage from './pages/command-center/LineItemsPage';

function RequireAuth({ children }) {
  const { token } = useAuth();
  // Replay the real URL after login. The display-facing useLocation strips a
  // tenant prefix, which would lose the restaurant from an emailed deep link.
  const location = useRawLocation();
  if (!token) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

// Effective role: while previewing as a lower role, admin-only routes are closed
// exactly as they are for that role.
function RequireAdmin({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/suppliers" replace />;
  return children;
}

// The Command Center gets its own guard rather than reusing RequireAdmin.
// A silent bounce to /suppliers is right inside the app, but wrong for a link
// someone was handed: it reads as a broken link and generates a support message.
// Say what happened instead.
function RequireAdminForCommandCenter({ children }) {
  // Real role: the console is never previewed (utils/viewAs.js).
  const { isRealAdmin } = useAuth();
  if (!isRealAdmin) {
    return (
      <div style={{ padding: '64px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.15rem', margin: '0 0 8px' }}>אין לך הרשאה למרכז הבקרה</h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', margin: '0 0 16px' }}>
          המרכז פתוח למשתמשי אדמין בלבד.
        </p>
        <Link className="btn btn-primary btn-sm" to="/suppliers">חזרה לאפליקציה</Link>
      </div>
    );
  }
  return children;
}

// Both gates again, for someone who reaches /dishes without the nav link —
// including bookmarks to a restaurant without the module. Checking BEFORE the page
// mounts is what keeps that from firing requests that come back 403; the
// api.js stale-tenant interceptor only handles the two supplier/document
// ownership errors and would let this one surface as a red toast.
function RequireDishModule({ children }) {
  const { canEdit } = useAuth();
  const { dishCostingEnabled, settingsLoaded } = useSettings();
  // Settings for the CURRENT restaurant haven't resolved yet (first load, or
  // just after a tenant switch — see SettingsContext's render-time reset).
  // dishCostingEnabled is still sitting at its closed default here, so
  // evaluating the gates now would show a legitimate admin, on an ENABLED
  // restaurant, a false "the module is off" message for the length of the
  // round trip. Rendering nothing is an honest blank instant; a wrong claim
  // on screen is not.
  if (!settingsLoaded) return null;
  if (!dishCostingEnabled || !canEdit) {
    // One message for two different closed gates (module off vs. role too
    // low), deliberately. Splitting it would mean telling a viewer whose
    // restaurant HAS the module that it exists and might be opened for them
    // — exactly the ask this policy exists to avoid. So the copy below stays
    // silent about WHICH gate closed and states only the true fact common to
    // both: this user doesn't have the screen. Do not reintroduce
    // restaurant-configuration wording here.
    return (
      <div style={{ padding: '64px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.15rem', margin: '0 0 8px' }}>עץ מוצר אינו זמין עבורך</h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', margin: '0 0 16px' }}>
          לשאלות על הגישה למסך זה פנו לצוות Peak.
        </p>
        <Link className="btn btn-primary btn-sm" to="/suppliers">חזרה לאפליקציה</Link>
      </div>
    );
  }
  return children;
}

function AppLayout() {
  const { toasts } = useToast();
  const { restaurantId } = useAuth();
  const location = useLocation();

  // SPA pageview: one $pageview per route change (autocapture pageview is off).
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname, restaurantId]);

  // The Command Center is a SIBLING shell, not a page inside this one: no
  // Navbar (therefore no tenant chip, which would be lying), and no
  // key={restaurantId} remount — console pages must survive another tab
  // switching customer. Same mechanism the auth screens already use.
  if (location.pathname === '/command-center' || location.pathname.startsWith('/command-center/')) {
    return (
      <div className="layout">
        <RequireAuth>
          <RequireAdminForCommandCenter>
            <CommandCenterLayout>
              <Routes>
                <Route path="/command-center"                    element={<CommandCenterOverviewPage />} />
                <Route path="/command-center/people"             element={<CommandCenterPeoplePage />} />
                <Route path="/command-center/people/:userId"     element={<CommandCenterUserCardPage />} />
                <Route path="/command-center/customers"          element={<CommandCenterCustomersPage />} />
                <Route path="/command-center/customers/:restaurantId" element={<CommandCenterCustomerCardPage />} />
                <Route path="/command-center/queues"             element={<CommandCenterQueuesPage />} />
                <Route path="/command-center/stats"              element={<CommandCenterStatsPage />} />
                <Route path="/command-center/business"           element={<CommandCenterBusinessPage />} />
                <Route path="/command-center/lines"              element={<CommandCenterLineItemsPage />} />
                <Route path="*" element={<Navigate to="/command-center" replace />} />
              </Routes>
            </CommandCenterLayout>
          </RequireAdminForCommandCenter>
        </RequireAuth>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  return (
    <div className="layout">
        <Routes>
          <Route path="/login" element={<main className="main-content"><LoginPage /></main>} />
          <Route path="/forgot-password" element={<main className="main-content"><ForgotPasswordPage /></main>} />
          <Route path="/reset-password/:token" element={<main className="main-content"><ResetPasswordPage /></main>} />
          <Route path="/r/:rid/*" element={<RequireAuth><TenantGate><SettingsProvider><CustomerShell /></SettingsProvider></TenantGate></RequireAuth>} />
          <Route path="*" element={<RequireAuth><LegacyRedirect /></RequireAuth>} />
        </Routes>
      <ToastContainer toasts={toasts} />
    </div>
  );
}

function CustomerShell() {
  const { restaurantId, user } = useAuth();
  return <>
    <Navbar />
    <ViewAsBanner />
    <main className="main-content" key={`${user?.id}:${restaurantId}`}>
      <Routes>
          <Route index element={<Navigate to="/suppliers" replace />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="supplier/:supplierId" element={<SupplierDocumentsPage />} />
          <Route path="document/:id" element={<DocumentViewPage />} />
          <Route path="upload" element={<RequireAdmin><UploadDocumentPage /></RequireAdmin>} />
          <Route path="analysis" element={<AnalysisPage />} />
          {/* The full by-supplier report — the workbench that used to live on
              /analysis. Reached from the SupplierDonutCard's לצפייה ← footer,
              which forwards the active date range as ?from=&to=. A flat route,
              no nesting: it is a standalone page, not a child layout. */}
          <Route path="analysis/reports/suppliers" element={<SupplierPurchaseReportPage />} />
          {/* Its by-classification twin — the table and the per-supplier drill that
              used to live inside the ClassificationAnalysis card. Reached from that
              card's לצפייה ← footer, which forwards ?from=&to= and, when the
              page's supplier filter is on, the supplier_ids behind it. */}
          <Route path="analysis/reports/item-classifications" element={<ItemClassificationReportPage />} />
          {/* Gated inside the page on top_products_enabled (it reads the settings
              context itself, like RequireDishModule does here). */}
          <Route path="analysis/reports/top-products" element={<TopProductsReportPage />} />
          {/* The third of the family, behind the RevenueVsBudgetPanel card.
              Same ?from=&to= contract; the page itself renders a module-off
              notice rather than 404ing, because a card may vanish when the
              flag is off but a page somebody navigated to may not. */}
          <Route path="analysis/reports/revenue" element={<RevenueVsExpensesReportPage />} />
          {/* Auth only, not admin: managers are the ones who actually tag, and
              viewers see it read-only (canEdit gates every control inside).
              Named /item-classifications after the page's own title (סיווגים
              פריטים), NOT /catalog — "קטלוג" already means editing a product's
              name/SKU/price elsewhere. /catalog kept as a redirect below. */}
          <Route path="item-classifications" element={<ItemClassificationPage />} />
          <Route path="catalog" element={<Navigate to="/item-classifications" replace />} />
          {/* RequireAuth, not RequireAdmin: everyone may SEE budget performance;
              editing is gated inside the page on canEdit, like /item-classifications.
              Renamed from /budget with the קטגוריות copy sweep, and — like the
              /item-categories → /item-classifications rename before it — WITHOUT
              a redirect, per owner. NOT /categories: that word belongs to the
              item-type axis in every database older than this deploy. */}
          <Route path="categories-management" element={<CategoriesManagementPage />} />
          <Route path="manage-suppliers" element={<RequireAdmin><SupplierManagementPage /></RequireAdmin>} />
          <Route path="suppliers/merge" element={<RequireAdmin><MergeSuppliersPage /></RequireAdmin>} />
          {/* Two tabs of one screen (CreditsViewTabs): still-owed vs already-
              resolved. Flat routes, no nesting — same shape as /suppliers +
              /suppliers/paid, chosen for the same reason: each view's status
              scope is locked server-side, so its cards and table can never
              disagree with each other. */}
          <Route path="credits" element={<CreditsOpenPage />} />
          <Route path="credits/history" element={<CreditsHistoryPage />} />
          {/* Payment-tracking is the "שולם" view of the suppliers screen, so it
              lives under /suppliers/* to make the parent/child relationship
              explicit in the URL (a flat route — no React Router nesting/Outlet
              needed). /payments is kept as a permanent redirect for old
              bookmarks and any external links. */}
          <Route path="suppliers/paid" element={<PaymentsPage />} />
          <Route path="payments" element={<Navigate to="/suppliers/paid" replace />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="dishes" element={<RequireDishModule><DishesPage /></RequireDishModule>} />
          <Route path="dishes/:id" element={<RequireDishModule><DishDetailPage /></RequireDishModule>} />
          <Route path="drafts" element={<RequireAdmin><DraftsPage /></RequireAdmin>} />
          <Route path="unpriced-items" element={<RequireAdmin><UnpricedItemsPage /></RequireAdmin>} />
          {/* The old admin panel is gone — the Command Center replaced every
              part of it. Kept as a permanent redirect for bookmarks and any
              link already shared, same treatment /payments got. */}
          <Route path="admin" element={<Navigate to="/command-center" replace />} />
          <Route path="settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </main>
    {/* Flow after opaque page content; auth screens have their own hero. */}
    <PageWatermark />
  </>;
}

function App() {
  return (
    <BrowserRouter>
      <TenantUrlProvider>
        <AuthProvider>
          <AppLayout />
        </AuthProvider>
      </TenantUrlProvider>
    </BrowserRouter>
  );
}

export default App;
